import partition from 'lodash/partition';
import moment from 'moment';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/map';

import { rosterApi } from '@modules/roster/api';
import { esbApiService, personNameBuilder } from '@utils';

import { UNIT_PARTICIPANT } from '../constants';
import { adultPackRosterDtoToModel, youthPackRosterDtoToModel } from '../utils';
import { dedupeMembers } from '../utilsTyped';

/**
 * @esbEndpoint GET /organizations/v2/units/:organizationGuid/adults
 */
const getAdults$ = (
  organizationGuid,
  suppressErrorToasts,
  /** @type {import('@reduxjs/toolkit').ThunkDispatch} */
  dispatch,
  forceRefetch,
) =>
  Observable.fromPromise(
    dispatch(
      rosterApi.endpoints.getUnitRosterAdults.initiate(
        { organizationGuid },
        { forceRefetch },
      ),
    ).then(result => {
      if (result.error) throw result;
      return result.data;
    }),
  );

/**
 * @esbEndpoint GET /organizations/v2/units/:organizationGuid/youths
 */
const getYouths$ = (
  /** @type {string} */
  organizationGuid,
  suppressErrorToasts,

  /** @type {import('@reduxjs/toolkit').ThunkDispatch} */
  dispatch,
  forceRefetch,
) => {
  const thunk = dispatch(
    rosterApi.endpoints.getUnitRosterYouths.initiate(
      {
        organizationGuid,
      },
      { forceRefetch },
    ),
  ).then(result => {
    if (result.error) throw result;
    return result.data;
  });

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint GET /organizations/v2/units/:organizationGuid/parents
 */
const getParents$ = (
  organizationGuid,
  suppressErrorToasts,
  /** @type {import('@reduxjs/toolkit').ThunkDispatch} */
  dispatch,
  forceRefetch,
) => {
  const thunk = dispatch(
    rosterApi.endpoints.getUnitRosterParents.initiate(
      {
        organizationGuid,
      },
      { forceRefetch },
    ),
  ).then(result => {
    if (result.error) throw result;
    return result.data;
  });

  return Observable.fromPromise(thunk);
};

const parentPackRosterDtoToModel = (roster = []) => {
  const getParentsOnly = roster.map(({ parentUserId, parentInformation }) => ({
    parentUserId,
    parentInformation,
  }));

  return getParentsOnly
    .filter(member => Number(member.parentUserId) > 0)
    .map(member => ({
      userId: Number(member.parentUserId),
      isAdult: true,
      isParent: true,
      isLeader: false,
      ...member.parentInformation,
      key3: member.parentInformation.key3 === 'True',
      personId: member.parentInformation.memberId,
      personShortFullName: personNameBuilder.short(member.parentInformation),
      age: member.parentInformation.age
        ? member.parentInformation.age
        : member.parentInformation.dateOfBirth &&
          moment().diff(member.parentInformation.dateOfBirth, 'years'),
      currentHighestRankApproved: { rank: '' },
      otherHighestRanksApproved: [],
      rosterOtherHighestRanksApproved: [],
    }));
};

const getPackRoster$ = ({
  organizationGuid,
  currentUnitTypeId,
  isSubUnitAllowed,
  ignoreSubUnits = false,
  dispatch,
  forceRefetch,
}) => {
  const isSubUnitRequest = isSubUnitAllowed && !ignoreSubUnits;
  const suppressErrorToasts = isSubUnitRequest;

  return getAdults$(
    organizationGuid,
    suppressErrorToasts,
    dispatch,
    forceRefetch,
  )
    .map(members => adultPackRosterDtoToModel(members))
    .switchMap(adultsResponse => {
      const [unitParticipants, adults] = partition(
        adultsResponse,
        adult => adult.position === UNIT_PARTICIPANT,
      );
      return Observable.forkJoin([
        getYouths$(
          organizationGuid,
          suppressErrorToasts,
          dispatch,
          forceRefetch,
        ).map(members =>
          youthPackRosterDtoToModel(
            members,
            currentUnitTypeId,
            unitParticipants,
          ),
        ),

        getParents$(
          organizationGuid,
          suppressErrorToasts,
          dispatch,
          forceRefetch,
        ).map(members => parentPackRosterDtoToModel(members)),

        Observable.of(adults),
      ]);
    })
    .map(([youths, adults, parents]) =>
      dedupeMembers([...youths, ...adults, ...parents]),
    );
};

/**
 * @esbEndpoint POST /advancements/v2/:organizationGuid/sbExtendedProfile
 */
const saveSBExtendedProfile$ = (data, organizationGuid) =>
  esbApiService.post$(
    `/advancements/v2/${organizationGuid}/sbExtendedProfile`,
    data,
    {
      gtm: {
        label: '/advancements/v2/{organizationGuid}/sbExtendedProfile',
      },
    },
  );

/**
 * @esbEndpoint GET /organizations/v2/:organizationGuid/orgYouths
 */
const getOrgYouths$ = (
  data,
  organizationGuid,
  /**
   * @type {import('@reduxjs/toolkit').ThunkDispatch}
   */
  dispatch,
) =>
  Observable.fromPromise(
    dispatch(
      rosterApi.endpoints.getOrgYouths.initiate({
        organizationGuid,
        body: data,
      }),
    )
      .unwrap()
      .then(res => res),
  );

/**
 * @esbEndpoint GET /organizations/v2/:organizationGuid/orgAdults
 */
const getOrgAdults$ = (data, organizationGuid, dispatch) =>
  Observable.fromPromise(
    dispatch(
      rosterApi.endpoints.getOrgAdults.initiate({
        organizationGuid,
        body: data,
      }),
    )
      .unwrap()
      .then(result => result),
  );

/**
 * @esbEndpoint PUT /persons/:personGuid/membershipRegistrations/:registrationId
 */
const saveMembershipRegistration$ = (data, personGuid, registrationId) =>
  esbApiService.put$(
    `/persons/${personGuid}/membershipRegistrations/${registrationId}`,
    data,
    {
      gtm: {
        label: '/persons/{personGuid}/membershipRegistrations/{registrationId}',
      },
    },
  );

export default {
  getPackRoster$,
  saveSBExtendedProfile$,
  getYouths$,
  getParents$,
  getAdults$,
  getOrgYouths$,
  getOrgAdults$,
  saveMembershipRegistration$,
};
